import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { Entity } from '@debbie/common/dist/index.js'
import { Case } from '@debbie/common/dist/cases/index.js'
import { courts } from '@debbie/common/dist/court/index.js'
import { getContactPerson } from '@debbie/common/dist/customers/index.js'
import { User } from '@debbie/common/dist/users/index.js'
import { c, h } from '@debbie/cortex/dist/index.js'
import { selectCaseById } from '@debbie/cortex/dist/cases/selectors.js'
import {
  selectCustomerById,
  selectCustomerIsLoadingById,
} from '@debbie/cortex/dist/customers/selectors.js'
import { useCreditor } from '@debbie/cortex/dist/creditors/index.js'
import { RootState } from '@debbie/cortex/dist/reducer.js'
import {
  Panel,
  PlaceholderLoader,
  RadioSelect,
} from '@debbie/iris/dist/components/index.js'
import CaseContactPerson from '@debbie/iris/dist/components/interactions/CaseContactPerson.js'
import CaseCreditor from '@debbie/iris/dist/components/interactions/CaseCreditor.js'
import Court from '@debbie/iris/dist/components/interactions/Court.js'
import CreditorContactPerson from '@debbie/iris/dist/components/interactions/CreditorContactPerson.js'
import Other from '@debbie/iris/dist/components/interactions/Other.js'

interface PropsCaseInteraction {
  referenceId: string
  referenceType: Entity
  outboundMode?: boolean
}

enum RecipientType {
  ContactPerson = 'CONTACT_PERSON',
  CaseCreditor = 'CASE_CREDITOR',
  Creditor = 'CREDITOR',
  Court = 'COURT',
  Other = 'OTHER',
}

const recipientTypeReferenceTypeMap: { [s: string]: { [s: string]: true } } = {
  [Entity.Case]: {
    [RecipientType.CaseCreditor]: true,
    [RecipientType.ContactPerson]: true,
    [RecipientType.Court]: true,
    [RecipientType.Other]: true,
  },
  [Entity.Creditor]: {
    [RecipientType.Creditor]: true,
    [RecipientType.Other]: true,
  },
}

const NewInteraction = (props: PropsCaseInteraction) => {
  const singleCase: Case | undefined | null = useSelector((s: RootState) =>
    selectCaseById(s, props.referenceId),
  )
  const customer = useSelector(
    (s: RootState) =>
      singleCase && selectCustomerById(s, singleCase.customerId),
  )
  const customerIsLoading = useSelector(
    (s: RootState) =>
      singleCase && selectCustomerIsLoadingById(s, singleCase.customerId),
  )
  const users = useSelector((s: RootState) => s.users)

  const creditorId =
    singleCase?.creditorId ||
    (props.referenceType === Entity.Creditor && props.referenceId)

  const creditorLoadable = useCreditor(creditorId || undefined)
  const creditor = h.hasData(creditorLoadable) ? creditorLoadable.data : null

  const userRes = creditor && creditor.contactId && users[creditor.contactId]

  const user: User | null = userRes && h.isLoaded(userRes) ? userRes.data : null

  const [recipient, setRecipient] = useState<RecipientType>(
    props.referenceType === Entity.Case
      ? RecipientType.ContactPerson
      : RecipientType.Creditor,
  )

  const lang = useSelector((s: RootState) => s.lang)
  const contactPerson = customer && getContactPerson(customer)

  const court = contactPerson?.details.courtId
    ? courts.find((c) => c.id === contactPerson?.details.courtId)
    : undefined

  const getLabel = (type: RecipientType) => {
    switch (type) {
      case RecipientType.Creditor:
        return user
          ? `${user.firstName} ${user.lastName}`
          : creditor && creditor.name
      case RecipientType.CaseCreditor:
        return creditor && creditor.name
      case RecipientType.Court:
        return court?.name || ''
      case RecipientType.ContactPerson:
        return contactPerson?.details?.name || lang.customers.noContact
      case RecipientType.Other:
        return lang.interactions.other
      default:
        return ''
    }
  }

  useEffect(() => {
    c.newInteractions.createNewInteraction(props.referenceId)
  }, [])

  if (props.referenceType === Entity.Case && !props.referenceId) return null

  const recipientTypeComponents = {
    [RecipientType.Creditor]: (
      <CreditorContactPerson creditorId={props.referenceId} />
    ),
    [RecipientType.CaseCreditor]: singleCase ? (
      <CaseCreditor case={singleCase} />
    ) : null,
    [RecipientType.ContactPerson]:
      contactPerson && singleCase ? (
        <CaseContactPerson
          case={singleCase}
          outboundMode={props.outboundMode}
        />
      ) : (
        <div>
          <h3>{lang.customers.noContactSelected}</h3>
        </div>
      ),
    [RecipientType.Court]: (
      <>
        {singleCase && customer && (
          <Court case={singleCase} customer={customer} />
        )}
      </>
    ),
    [RecipientType.Other]: (
      <Other
        referenceId={props.referenceId}
        referenceType={props.referenceType}
      />
    ),
  }

  return (
    <Panel header={lang.interactions.newInteractions}>
      {customerIsLoading ? (
        <PlaceholderLoader />
      ) : (
        <>
          {Object.values(RecipientType).map((type) => {
            if (!recipientTypeReferenceTypeMap[props.referenceType][type]) {
              return null
            }

            return (
              (court || type !== RecipientType.Court) && (
                <RadioSelect
                  active={recipient === type}
                  key={getLabel(type)}
                  label={getLabel(type)}
                  onClick={() => setRecipient(type)}
                />
              )
            )
          })}
          {recipientTypeComponents[recipient]}
        </>
      )}
    </Panel>
  )
}

export default NewInteraction
